import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchExpensesForDateRange,
  fetchCoaZReportExpensesForDateRange,
  fetchCoaPettyCashLedgerPaymentsForDateRange,
} from '../../services/admin/financeLedgerApi';
import { fetchZReportTotalsRpc } from '../../services/admin/zReportRpcApi';
import { ensureGymId } from '../../services/receptionist/gymContext';
import { isReceptionistPanelScope } from '../../services/gym/branchContext';
import { pkTodayKey } from '../../utils/pkTime';
import {
  buildDailyZSheetAuto,
  localDateKey,
  mapApiExpenseToManual,
  mergeManualExpenses,
  expenseCalendarDay,
} from '../admin/finance/financeData';

/** Inclusive 7-day window ending on reportDay. */
export function weekSaleRangeFor(reportDay) {
  const end = String(reportDay || localDateKey()).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    const today = localDateKey();
    return { start: today, end: today };
  }
  const d = new Date(`${end}T12:00:00`);
  d.setDate(d.getDate() - 6);
  return { start: localDateKey(d), end };
}

/** Receptionist: PK today only. Admin: 7-day window ending on report day. */
function saleRangeFor(reportDay) {
  if (isReceptionistPanelScope()) {
    const today = pkTodayKey();
    return { start: today, end: today };
  }
  return weekSaleRangeFor(reportDay);
}

const totalsCache = new Map();
const expensesCache = new Map();
const cacheListeners = new Set();

/** Bust cached range fetches after new sales / payments. */
export function clearZReportRangeCache() {
  totalsCache.clear();
  expensesCache.clear();
  cacheListeners.forEach((fn) => fn());
}

export function subscribeZReportRangeCache(listener) {
  cacheListeners.add(listener);
  return () => cacheListeners.delete(listener);
}

async function fetchTotalsBundle(from, to) {
  const key = `${from}|${to}`;
  if (totalsCache.has(key)) return totalsCache.get(key);

  const pending = (async () => {
    const gid = await ensureGymId().catch(() => '');
    const rpcAuto = await fetchZReportTotalsRpc(from, to, gid || undefined).catch(() => null);
    return { rpcAuto, gymId: gid || '' };
  })();

  totalsCache.set(key, pending);
  try {
    return await pending;
  } catch (err) {
    totalsCache.delete(key);
    throw err;
  }
}

async function fetchExpensesBundle(from, to) {
  const key = `${from}|${to}`;
  if (expensesCache.has(key)) return expensesCache.get(key);

  const pending = (async () => {
    const gid = await ensureGymId().catch(() => '');
    const [expenseRows, coaRows, pettyRows] = await Promise.all([
      fetchExpensesForDateRange(from, to, gid || undefined).catch(() => []),
      fetchCoaZReportExpensesForDateRange(from, to, gid || undefined).catch(() => []),
      fetchCoaPettyCashLedgerPaymentsForDateRange(from, to, gid || undefined).catch(() => []),
    ]);
    const inRange = (row) => {
      const d = expenseCalendarDay(row);
      return d && d >= from && d <= to;
    };
    const apiExpenses = (expenseRows || []).map(mapApiExpenseToManual).filter(inRange);
    const coaMapped = (coaRows || []).map(mapApiExpenseToManual).filter(inRange);
    const coaPettyMapped = (pettyRows || []).map(mapApiExpenseToManual).filter(inRange);
    const expenses = mergeManualExpenses(
      mergeManualExpenses([...apiExpenses, ...coaMapped], coaPettyMapped),
      [],
    );
    return { expenses, gymId: gid || '' };
  })();

  expensesCache.set(key, pending);
  try {
    return await pending;
  } catch (err) {
    expensesCache.delete(key);
    throw err;
  }
}

function buildExpenseAuto(start, end, reportDate, expenses, gymId) {
  return buildDailyZSheetAuto({
    date: end || reportDate,
    dateFrom: start,
    dateTo: end || reportDate,
    payments: [],
    guestPassIncome: [],
    expenses,
    gymId,
    paymentsAlreadyScoped: true,
  });
}

function buildTotalsAuto(start, end, reportDate, rpcAuto, expenses, gymId) {
  const expenseAuto = buildExpenseAuto(start, end, reportDate, expenses, gymId);
  if (!rpcAuto) return expenseAuto;
  return {
    ...expenseAuto,
    ...rpcAuto,
    cashExpenses: expenseAuto.cashExpenses,
    bankExpenses: expenseAuto.bankExpenses,
    loanCashExpenses: expenseAuto.loanCashExpenses,
    loanBankExpenses: expenseAuto.loanBankExpenses,
    paymentPending: expenseAuto.paymentPending,
    expenseTotal: expenseAuto.expenseTotal,
  };
}

const RANGE_DEBOUNCE_MS = 450;

/**
 * Shared date range + parallel loads for all bottom Z tables.
 * Totals (sale summary / payment methods) use RPC only — not blocked by expenses.
 */
export function useZReportRanges(reportDate, gymId = '') {
  const initial = saleRangeFor(reportDate);
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [debouncedRange, setDebouncedRange] = useState({ start: initial.start, end: initial.end });
  const [dataRange, setDataRange] = useState({ start: initial.start, end: initial.end });
  const [rpcAuto, setRpcAuto] = useState(null);
  const [reportDayRpc, setReportDayRpc] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [totalsLoading, setTotalsLoading] = useState(true);
  const [expensesLoading, setExpensesLoading] = useState(true);
  const [totalsRefreshing, setTotalsRefreshing] = useState(false);
  const [expensesRefreshing, setExpensesRefreshing] = useState(false);
  const [reportDayLoading, setReportDayLoading] = useState(true);
  const [reportDayRefreshing, setReportDayRefreshing] = useState(false);
  const [cacheTick, setCacheTick] = useState(0);
  const prevReportRef = useRef(reportDate);
  const hadTotalsRef = useRef(false);
  const hadExpensesRef = useRef(false);
  const hadReportDayRef = useRef(false);

  useEffect(() => subscribeZReportRangeCache(() => setCacheTick((t) => t + 1)), []);

  useEffect(() => {
    if (prevReportRef.current === reportDate) return;
    prevReportRef.current = reportDate;
    const next = saleRangeFor(reportDate);
    setStart(next.start);
    setEnd(next.end);
    setDebouncedRange(next);
  }, [reportDate]);

  useEffect(() => {
    if (!isReceptionistPanelScope()) return;
    const today = pkTodayKey();
    if (start !== today || end !== today) {
      setStart(today);
      setEnd(today);
      setDebouncedRange({ start: today, end: today });
    }
  }, [start, end]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRange({ start, end });
    }, RANGE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [start, end]);

  useEffect(() => {
    let cancelled = false;
    const from = String(debouncedRange.start || '').slice(0, 10);
    const to = String(debouncedRange.end || debouncedRange.start || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) {
      setRpcAuto(null);
      setExpenses([]);
      setTotalsLoading(false);
      setExpensesLoading(false);
      setTotalsRefreshing(false);
      setExpensesRefreshing(false);
      return undefined;
    }

    if (hadTotalsRef.current) setTotalsRefreshing(true);
    else setTotalsLoading(true);

    (async () => {
      try {
        const bundle = await fetchTotalsBundle(from, to);
        if (cancelled) return;
        setRpcAuto(bundle.rpcAuto || null);
        setDataRange((prev) => (
          prev.start === from && prev.end === to ? prev : { start: from, end: to }
        ));
        hadTotalsRef.current = Boolean(bundle.rpcAuto);
      } catch {
        if (!cancelled) setRpcAuto(null);
      } finally {
        if (!cancelled) {
          setTotalsLoading(false);
          setTotalsRefreshing(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [debouncedRange.start, debouncedRange.end, cacheTick]);

  // Report-day totals (PAYMENT METHOD + CASH SALE) — always single calendar day
  useEffect(() => {
    let cancelled = false;
    const day = String(reportDate || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
      setReportDayRpc(null);
      setReportDayLoading(false);
      setReportDayRefreshing(false);
      return undefined;
    }

    if (hadReportDayRef.current) setReportDayRefreshing(true);
    else setReportDayLoading(true);

    (async () => {
      try {
        const bundle = await fetchTotalsBundle(day, day);
        if (cancelled) return;
        setReportDayRpc(bundle.rpcAuto || null);
        hadReportDayRef.current = Boolean(bundle.rpcAuto);
      } catch {
        if (!cancelled) setReportDayRpc(null);
      } finally {
        if (!cancelled) {
          setReportDayLoading(false);
          setReportDayRefreshing(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [reportDate, cacheTick]);

  useEffect(() => {
    let cancelled = false;
    const from = String(debouncedRange.start || '').slice(0, 10);
    const to = String(debouncedRange.end || debouncedRange.start || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) {
      return undefined;
    }

    if (hadExpensesRef.current) setExpensesRefreshing(true);
    else setExpensesLoading(true);

    (async () => {
      try {
        const bundle = await fetchExpensesBundle(from, to);
        if (cancelled) return;
        setExpenses(bundle.expenses || []);
        setDataRange((prev) => (
          prev.start === from && prev.end === to ? prev : { start: from, end: to }
        ));
        hadExpensesRef.current = (bundle.expenses || []).length >= 0;
      } catch {
        if (!cancelled) setExpenses([]);
      } finally {
        if (!cancelled) {
          setExpensesLoading(false);
          setExpensesRefreshing(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [debouncedRange.start, debouncedRange.end, cacheTick]);

  const totalsAuto = useMemo(
    () => buildTotalsAuto(dataRange.start, dataRange.end, reportDate, rpcAuto, expenses, gymId),
    [dataRange.start, dataRange.end, reportDate, rpcAuto, expenses, gymId],
  );

  const expenseAuto = useMemo(
    () => buildExpenseAuto(dataRange.start, dataRange.end, reportDate, expenses, gymId),
    [dataRange.start, dataRange.end, reportDate, expenses, gymId],
  );

  const reportDayAuto = useMemo(() => {
    if (!reportDayRpc) return null;
    return buildTotalsAuto(reportDate, reportDate, reportDate, reportDayRpc, [], gymId);
  }, [reportDate, reportDayRpc, gymId]);

  const rangeBusy = totalsLoading || expensesLoading;
  const rangeRefreshing = totalsRefreshing || expensesRefreshing;

  const setStartSafe = (value) => {
    if (isReceptionistPanelScope()) {
      const today = pkTodayKey();
      setStart(today);
      setEnd(today);
      return;
    }
    setStart(value);
  };

  const setEndSafe = (value) => {
    if (isReceptionistPanelScope()) {
      const today = pkTodayKey();
      setStart(today);
      setEnd(today);
      return;
    }
    setEnd(value);
  };

  const makeRangeView = (autoSlice) => ({
    start: isReceptionistPanelScope() ? pkTodayKey() : start,
    end: isReceptionistPanelScope() ? pkTodayKey() : end,
    dataStart: dataRange.start,
    dataEnd: dataRange.end,
    setStart: setStartSafe,
    setEnd: setEndSafe,
    loading: rangeBusy,
    refreshing: rangeRefreshing,
    auto: autoSlice,
  });

  return {
    start,
    end,
    setStart: setStartSafe,
    setEnd: setEndSafe,
    totalsLoading,
    expensesLoading,
    totalsRefreshing,
    expensesRefreshing,
    totalsAuto,
    expenseAuto,
    reportDayAuto,
    reportDayLoading,
    reportDayRefreshing,
    totalSaleRange: makeRangeView(totalsAuto),
    totalMethodRange: makeRangeView(totalsAuto),
    expenseRange: makeRangeView(expenseAuto),
  };
}

/** @deprecated Use useZReportRanges — kept for any external imports */
export function useZTableRange(reportDate, gymId = '') {
  const ranges = useZReportRanges(reportDate, gymId);
  return ranges.totalSaleRange;
}
